import { useEffect, useState } from "react";
import { Check, Loader2, Sparkles, Users, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const UsageBar = ({ label, used, limit, pct }) => (
  <div data-testid={`usage-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex justify-between text-xs mb-1.5">
      <span className="text-[#757675]">{label}</span>
      <span className="text-[#1A1B1A] font-medium">
        {used} {limit ? `of ${limit}` : "used"}
      </span>
    </div>
    <div className="h-1.5 rounded-full bg-[#1A1B1A]/[0.06] overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-[#205B5A]"
        }`}
        style={{ width: `${Math.min(100, pct || 0)}%` }}
      />
    </div>
  </div>
);

export const BillingPlanPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    const { data } = await api.get("/billing/plan");
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const requestUpgrade = async (planId) => {
    setBusy(planId);
    try {
      const { data: res } = await api.post("/billing/upgrade-request", { plan_id: planId });
      setMessage(res.message);
      await load();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Could not send the upgrade request. Please try again.");
    }
    setBusy("");
  };

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#757675]" data-testid="billing-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your plan…
      </div>
    );
  }

  const { subscription: sub, practitioners, ai, plans, pending_upgrade } = data;
  const currentIndex = plans.findIndex((p) => p.id === sub.plan_id);

  return (
    <div className="space-y-6" data-testid="billing-plan-panel">
      {/* Current plan */}
      <div className="bg-[#131E1E] text-white rounded-2xl p-7">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-2">Current plan</p>
            <h3 className="text-2xl font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="billing-current-plan">
              {sub.plan_name}
            </h3>
            <p className="text-sm text-white/50 mt-1" data-testid="billing-price">
              {sub.price_gbp === null ? "Custom pricing"
                : sub.price_gbp === 0 ? "Internal plan"
                : `£${sub.price_gbp}/month`}
            </p>
          </div>
          <div className="text-sm space-y-1.5 text-white/70">
            <p className="flex items-center gap-2" data-testid="billing-status">
              <span className={`w-1.5 h-1.5 rounded-full ${
                sub.subscription_status === "active" ? "bg-[#7EBDBC]"
                : sub.subscription_status === "trialing" ? "bg-amber-400" : "bg-red-400"}`} />
              {sub.subscription_status}
              {sub.trial_days_left !== null && sub.subscription_status === "trialing"
                ? ` · ${sub.trial_days_left} days left in trial` : ""}
            </p>
            <p className="flex items-center gap-2" data-testid="billing-seats">
              <Users className="w-3.5 h-3.5" />
              {practitioners.used} of {practitioners.limit ?? "unlimited"} practitioners used
            </p>
            <p className="flex items-center gap-2" data-testid="billing-ai-status">
              <Sparkles className="w-3.5 h-3.5" />
              {sub.ai_documentation_enabled
                ? "AI documentation included with fair usage"
                : "AI documentation not included"}
            </p>
          </div>
        </div>
      </div>

      {!ai.allowed && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-3"
          data-testid="billing-ai-blocked">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{ai.reason}</span>
        </div>
      )}

      {/* Usage */}
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 space-y-5"
        data-testid="billing-usage">
        <div>
          <h4 className="text-sm font-semibold text-[#1A1B1A]">AI usage this month</h4>
          <p className="text-xs text-[#757675] mt-1">
            Fair usage keeps AI documentation included in your plan. We'll warn you before you run out.
          </p>
        </div>
        <UsageBar label="AI notes" used={ai.usage.notes_used} limit={ai.usage.notes_limit} pct={ai.usage.notes_pct} />
        <UsageBar label="Audio minutes" used={ai.usage.minutes_used} limit={ai.usage.minutes_limit} pct={ai.usage.minutes_pct} />
        {ai.warn_at_80 && ai.allowed && (
          <p className="text-xs text-amber-700" data-testid="billing-usage-warning">
            You've used over 80% of your fair usage this month. Contact support if you need a higher limit.
          </p>
        )}
      </div>

      {/* Upgrade */}
      {message && (
        <div className="rounded-xl bg-[#205B5A]/[0.08] border border-[#205B5A]/20 p-4 text-sm text-[#205B5A]"
          data-testid="billing-upgrade-message">
          {message}
        </div>
      )}
      {pending_upgrade && !message && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800"
          data-testid="billing-pending-upgrade">
          Upgrade to {pending_upgrade.requested_plan_name} requested. Our team will review it shortly.
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-[#1A1B1A] mb-4">Plans</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan, i) => {
            const isCurrent = plan.id === sub.plan_id;
            const isUpgrade = currentIndex >= 0 && i > currentIndex;
            return (
              <div key={plan.id} data-testid={`billing-plan-${plan.id}`}
                className={`rounded-2xl p-5 border flex flex-col ${
                  isCurrent ? "border-[#205B5A] bg-[#205B5A]/[0.04]" : "border-[#1A1B1A]/[0.06] bg-white"}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-sm font-semibold text-[#1A1B1A]">{plan.name}</p>
                  <p className="text-sm text-[#1A1B1A]">
                    {plan.price_gbp === null ? "Custom" : `£${plan.price_gbp}`}
                    {plan.price_gbp !== null && <span className="text-[#757675] text-xs">/mo</span>}
                  </p>
                </div>
                <p className="text-xs text-[#757675] mb-4">
                  {plan.practitioner_limit ? `Up to ${plan.practitioner_limit} practitioner${plan.practitioner_limit > 1 ? "s" : ""}` : "Custom practitioners"}
                  {" · AI included with fair usage"}
                </p>
                {isCurrent ? (
                  <span className="mt-auto flex items-center justify-center gap-1.5 text-xs font-medium text-[#205B5A] py-2.5">
                    <Check className="w-3.5 h-3.5" /> Your plan
                  </span>
                ) : plan.id === "enterprise" ? (
                  <a href="mailto:support@lumeroflow.co.uk?subject=Enterprise%20plan%20enquiry"
                    data-testid="billing-contact-enterprise"
                    className="mt-auto text-center text-xs font-medium py-2.5 rounded-full border border-[#1A1B1A]/10 text-[#1A1B1A] hover:bg-[#1A1B1A]/[0.04] transition-colors">
                    Contact us
                  </a>
                ) : (
                  <button onClick={() => requestUpgrade(plan.id)} disabled={busy === plan.id}
                    data-testid={`billing-upgrade-${plan.id}-btn`}
                    className="mt-auto text-xs font-medium py-2.5 rounded-full bg-[#205B5A] text-white hover:bg-[#174544] transition-colors disabled:opacity-50">
                    {busy === plan.id ? "Sending…" : isUpgrade ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#757675] mt-4">
          Upgrades are reviewed by our team and applied to your account. Need a non-AI clinic plan?{" "}
          <a href="mailto:support@lumeroflow.co.uk" className="text-[#205B5A] hover:underline">Contact us</a>.
        </p>
      </div>
    </div>
  );
};
